import axios from "axios";
import { logout } from "utils/accounts";
import Cookies from "js-cookie";
import Bugsnag from "@bugsnag/js";

let interceptorsRegistered = false;
let csrfBootstrapPromise = null;

const leaveCsrfBreadcrumb = (message, metadata = {}) => {
  console.warn(`[API] ${message}`, metadata);

  if (typeof Bugsnag?.leaveBreadcrumb === "function") {
    Bugsnag.leaveBreadcrumb(message, metadata, "request");
  }
};

const notifyCsrfFailure = (message, metadata = {}) => {
  leaveCsrfBreadcrumb(message, metadata);

  if (typeof Bugsnag?.notify === "function") {
    Bugsnag.notify(new Error(message), (event) => {
      event.addMetadata("csrf", metadata);
    });
  }
};

export const getCSRFToken = async (force = false) => {
  const CSRFToken = Cookies.get("XSRF-TOKEN");

  if (CSRFToken && !force) return CSRFToken;

  try {
    const res = await axios("/csrf-token");

    if (res.data && res.data.token) {
      // NOTE: (Andrey) add CSRF token to cookie without domain to avoid add dot before domain
      Cookies.set("XSRF-TOKEN", res.data.token, {
        path: "/",
      });

      return res.data.token;
    }
  } catch (error) {
    console.log("Get CSRF Token Error", error);
    throw error;
  }
};

export const initAPI = async () => {
  const CSRF_TOKEN_MAX_RETRY = 3;

  // Remove deprecated global CSRF token and tokens with dot, for now we use separated token for each hostname without dot (domain)
  Cookies.remove("XSRF-TOKEN", { domain: ".skedsocial.com" });
  Cookies.remove("XSRF-TOKEN", { domain: ".app.skedsocial.com" });
  Cookies.remove("XSRF-TOKEN", { domain: ".staging.skedsocial.com" });
  Cookies.remove("XSRF-TOKEN", { domain: ".staging-n18.skedsocial.com" });

  if (!interceptorsRegistered) {
    interceptorsRegistered = true;

    axios.interceptors.response.use(null, async function (error) {
      // NOTE: (Andrey) catch bad CSRF token errors
      if (
        error?.response?.status === 403 &&
        error?.response?.data?.csrfReset === 1
      ) {
        const requestUrl = error?.config?.url || "unknown";
        const csrfTokenRetry = error?.config?._csrfTokenRetry || 0;

        // NOTE: (Andrey) If token is wrong try get new one only 3 times (avoid infinity getting token)
        if (csrfTokenRetry >= CSRF_TOKEN_MAX_RETRY) {
          notifyCsrfFailure("CSRF retry limit reached", {
            requestUrl,
            maxRetries: CSRF_TOKEN_MAX_RETRY,
          });

          throw error;
        }

        const attempt = csrfTokenRetry + 1;
        leaveCsrfBreadcrumb("Retrying request after CSRF reset", {
          attempt,
          requestUrl,
        });

        try {
          // NOTE: (Andrey) Try force get new token
          const csrfToken = await getCSRFToken(true);

          if (!csrfToken) {
            throw new Error("CSRF refresh did not return a token");
          }

          const retryConfig = {
            ...error.config,
            _csrfTokenRetry: attempt,
          };

          if (retryConfig.headers?.set) {
            retryConfig.headers.set("X-XSRF-TOKEN", csrfToken);
          } else {
            retryConfig.headers = {
              ...retryConfig.headers,
              "X-XSRF-TOKEN": csrfToken,
            };
          }

          // NOTE: (Andrey) retry request with new token
          const response = await axios(retryConfig);
          leaveCsrfBreadcrumb("CSRF retry succeeded", {
            attempt,
            requestUrl,
          });
          return response;
        } catch (retryError) {
          notifyCsrfFailure("CSRF retry failed", {
            attempt,
            requestUrl,
            errorMessage: retryError?.message,
          });
          throw retryError;
        }
      }

      // NOTE: (Andrey) catch unauthorized and logout, go to login page
      // Skip logout for /api-jwt — that 401 is handled by the token refresh
      // retry logic in _tokenRefresh.js and should degrade gracefully.
      if (error?.response?.status === 401) {
        const requestUrl = error?.config?.url || "";
        const isBootstrapRedirect =
          requestUrl === "/user/bootstrap" &&
          error?.response?.data?.type === "bootstrap_redirect";
        if (!requestUrl.includes("/api-jwt") && !isBootstrapRedirect) {
          logout();
        }
      }

      if (error?.response?.status >= 500 && Bugsnag) {
        Bugsnag.notify(error);
      }

      throw error;
    });
  }

  if (!csrfBootstrapPromise) {
    csrfBootstrapPromise = getCSRFToken().catch((error) => {
      csrfBootstrapPromise = null;
      notifyCsrfFailure("Initial CSRF bootstrap failed", {
        errorMessage: error?.message,
      });
      throw error;
    });
  }

  return csrfBootstrapPromise;
};
